import React, { useCallback, useContext, useEffect, useState } from 'react';
import DealRoomContextContext from './DealRoom.context';
import api from 'api';
import { getIsListingOwner } from 'utils';
import { BUSINESS_ROUTE } from 'data/global';
import { DealRoom } from 'models/DealRoom';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import FourDotLoader from 'components/FourDotLoader/FourDotLoader.component';
import useColleagues from 'hooks/useColleagues';

const Deal: React.FC = (props) => {
  const { children } = props;

  const [dealRoom, setDealRoom] = useState<DealRoom>();
  const [isLoading, setIsLoading] = useState(true);

  const {
    currentUser: { _id: currentUserId },
    isSuperAdmin,
  } = useContext(CurrentUserContext);
  const { popServerError } = useContext(PopNotificationsContext);

  const { id: dealRoomId } = useParams();

  const navigate = useNavigate();

  const { isLoading: areColleaguesLoading, colleagues } =
    useColleagues(dealRoomId);

  const updateDealRoom = useCallback(
    (dealRoom: Partial<DealRoom>) =>
      setDealRoom((old) => ({ ...old, ...dealRoom })),
    [],
  );

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        const {
          data: { revenueShares, ...restData },
        } = await api.dealRoom.getRoom(dealRoomId);

        const canEditDeal =
          isSuperAdmin ||
          getIsListingOwner(
            currentUserId,
            restData?.listing?.userId,
            restData?.listing?.isCoOwner,
          );

        if (!canEditDeal) navigate(BUSINESS_ROUTE);

        const filteredRevenueValues = revenueShares?.map(
          ({ description, orderNumber, percentage, email, label }) => ({
            description,
            orderNumber,
            email,
            percentage,
            label,
          }),
        );

        setDealRoom({ ...restData, revenueShares: filteredRevenueValues });
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [currentUserId, dealRoomId, isSuperAdmin, navigate, popServerError]);

  if (isLoading || areColleaguesLoading) return <FourDotLoader />;

  if (!dealRoom) return <Navigate to={BUSINESS_ROUTE} replace />;

  return (
    <DealRoomContextContext.Provider
      value={{
        dealRoom,
        isLoading,
        colleagues,
        areColleaguesLoading,
        updateDealRoom,
      }}
    >
      {children}
    </DealRoomContextContext.Provider>
  );
};

export default Deal;
